import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Checkbox, Group, Loader, Select, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import styled from '@emotion/styled';
import FetchUtils from '@dr-pam/common-components/Utils/FetchUtils';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { KlaviyoMetricType } from '@dr-pam/common-components/Enums/KlaviyoMetricType';
import type { KlaviyoTestEventFieldRequirements } from '@dr-pam/common-components/Utils/KlaviyoTestEventMetadata';
import {
	type ActivityLogEntry,
	computeSuggestedValidTo,
	findRequirements,
	initialValues,
	type KlaviyoTestFormValues,
	type KlaviyoTestMetadataResponse,
	labelFor,
	resolveSubscriptionTypeIdOnProductChange,
} from './KlaviyoTestFormTypes';
import ProductPlanFields from './ProductPlanFields';
import FromProductPlanFields from './FromProductPlanFields';
import DateRangeFields from './DateRangeFields';
import NewsletterFields from './NewsletterFields';
import { PaymentFailureFields, PaymentFields } from './PaymentFields';
import {
	AccessProductField,
	EventOccurrenceField,
	ProgrammeField,
	QuizFields,
	RegistrantTimezoneField,
} from './ProgrammeQuizFields';
import KlaviyoTestActivityLog from './KlaviyoTestActivityLog';

export type {
	KlaviyoTestMetadataProduct,
	KlaviyoTestMetadataProductSubscriptionType,
	KlaviyoTestMetadataProgramme,
	KlaviyoTestMetadataQuiz,
	KlaviyoTestMetadataResponse,
} from './KlaviyoTestFormTypes';

export type KlaviyoTestFormProps = {
	className?: string;
};

export default function KlaviyoTestForm(props: KlaviyoTestFormProps) {
	const { className } = props;

	const { isLoading, addLoader, removeLoader } = useLoadTracker();

	const [metadata, setMetadata] = useState<KlaviyoTestMetadataResponse | null>(null);
	const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);

	useEffect(() => {
		const loader = addLoader();
		const request = FetchUtils.getJson<KlaviyoTestMetadataResponse>('/api/admin/klaviyo-test/metadata');
		request.response
			.then((response) => setMetadata(response))
			.catch((err) => NotificationUtils.showError(err as Error, 'Failed to load Klaviyo test event metadata'))
			.finally(() => removeLoader(loader));
	}, [addLoader, removeLoader]);

	const form = useForm<KlaviyoTestFormValues>({
		initialValues,
		validate: (values) => {
			const requirements = findRequirements(metadata, values.metric);
			const errors: Partial<Record<keyof KlaviyoTestFormValues, string>> = {};

			if (!values.metric) {
				errors.metric = 'Select an event to trigger';
			}
			if (!values.email) {
				errors.email = 'Email is required';
			}
			if (requirements?.needsProduct) {
				if (!values.productId) {
					errors.productId = 'Required';
				}
				if (!values.productSubscriptionTypeId) {
					errors.productSubscriptionTypeId = 'Required';
				}
			}
			if (requirements?.needsAccessProduct && !values.productId) {
				errors.productId = 'Required';
			}
			if (requirements?.needsFromProduct) {
				if (!values.fromProductId) {
					errors.fromProductId = 'Required';
				}
				if (!values.fromProductSubscriptionTypeId) {
					errors.fromProductSubscriptionTypeId = 'Required';
				}
			}
			if (requirements?.needsDates) {
				if (!values.validFrom) {
					errors.validFrom = 'Required';
				}
				if (!values.validTo) {
					errors.validTo = 'Required';
				}
			}
			if (requirements?.needsResetUrl && !values.resetUrl) {
				errors.resetUrl = 'Required';
			}
			if (requirements?.needsPayment && !values.paymentId) {
				errors.paymentId = 'Required — paste an existing Payment ID';
			}
			if (requirements?.needsProgramme && !values.programmeId) {
				errors.programmeId = 'Required';
			}
			if (requirements?.needsEventOccurrence && !values.eventOccurrenceId) {
				errors.eventOccurrenceId = 'Required';
			}
			if (requirements?.needsQuiz) {
				if (!values.quizId) {
					errors.quizId = 'Required';
				}
				if (values.score === '') {
					errors.score = 'Required';
				}
			}

			return errors;
		},
	});

	const requirements = findRequirements(metadata, form.values.metric);

	const metricOptions = useMemo(() => groupMetricsByCategory(metadata?.metrics ?? []), [metadata?.metrics]);
	const productOptions = useMemo(
		() => (metadata?.products ?? []).map((product) => ({ value: product.id, label: product.name })),
		[metadata?.products],
	);
	const accessProductOptions = useMemo(
		() =>
			(metadata?.products ?? [])
				.filter((product) =>
					product.productProgrammes.some(
						(productProgramme) => productProgramme.programmeId === form.values.programmeId,
					),
				)
				.map((product) => ({ value: product.id, label: product.name })),
		[form.values.programmeId, metadata?.products],
	);
	const programmeOptions = useMemo(
		() => (metadata?.programmes ?? []).map((programme) => ({ value: programme.id, label: programme.name })),
		[metadata?.programmes],
	);
	const quizOptions = useMemo(
		() => (metadata?.quizzes ?? []).map((quiz) => ({ value: quiz.id, label: quiz.title })),
		[metadata?.quizzes],
	);
	const eventOccurrenceOptions = useMemo(
		() =>
			(metadata?.eventOccurrences ?? []).map((occurrence) => ({
				value: occurrence.id,
				label: `${occurrence.event.title} — ${dayjs(occurrence.start).format('ddd D MMM YYYY, h:mm a')}`,
			})),
		[metadata?.eventOccurrences],
	);

	const selectedProduct = (metadata?.products ?? []).find((product) => product.id === form.values.productId);
	const productSubscriptionTypeOptions = useMemo(
		() => (selectedProduct?.productSubscriptionTypes ?? []).map((pst) => ({ value: pst.id, label: pst.name })),
		[selectedProduct],
	);
	const selectedProductSubscriptionType = selectedProduct?.productSubscriptionTypes.find(
		(pst) => pst.id === form.values.productSubscriptionTypeId,
	);

	const selectedFromProduct = (metadata?.products ?? []).find((product) => product.id === form.values.fromProductId);
	const fromProductSubscriptionTypeOptions = useMemo(
		() => (selectedFromProduct?.productSubscriptionTypes ?? []).map((pst) => ({ value: pst.id, label: pst.name })),
		[selectedFromProduct],
	);

	const handleMetricChange = (metric: string | null) => {
		form.setValues({ ...initialValues, email: form.values.email, metric: metric as KlaviyoMetricType | null });
	};

	/**
	 * Resets `productSubscriptionTypeId` synchronously when the product changes, rather than via
	 * a `useEffect` that watches `productId` and reads `selectedProduct` from a stale closure.
	 */
	const handleProductIdChange = (value: string | null) => {
		form.setValues({
			productId: value,
			productSubscriptionTypeId: resolveSubscriptionTypeIdOnProductChange(
				metadata?.products ?? [],
				value,
				form.values.productSubscriptionTypeId,
			),
		});
	};

	/** Same reset as {@link handleProductIdChange}, for the "from product" pair of fields. */
	const handleFromProductIdChange = (value: string | null) => {
		form.setValues({
			fromProductId: value,
			fromProductSubscriptionTypeId: resolveSubscriptionTypeIdOnProductChange(
				metadata?.products ?? [],
				value,
				form.values.fromProductSubscriptionTypeId,
			),
		});
	};

	const handleProgrammeIdChange = (value: string | null) => {
		const selectedProduct = (metadata?.products ?? []).find((product) => product.id === form.values.productId);
		const productStillContainsProgramme = selectedProduct?.productProgrammes.some(
			(productProgramme) => productProgramme.programmeId === value,
		);
		form.setValues({
			programmeId: value,
			...(!productStillContainsProgramme ? { productId: null } : {}),
		});
	};

	/**
	 * Suggests a `validTo` date when the subscription type changes, using whatever `validFrom` is
	 * already entered. Synchronous counterpart to the removed valid-from-driven effect below.
	 */
	const handleProductSubscriptionTypeIdChange = (value: string | null) => {
		const newSubscriptionType = selectedProduct?.productSubscriptionTypes.find((pst) => pst.id === value);
		const suggestedValidTo = computeSuggestedValidTo(
			form.values.validFrom,
			form.values.validTo,
			newSubscriptionType?.durationInDays,
			newSubscriptionType?.isTrialSubscription,
		);
		form.setValues({
			productSubscriptionTypeId: value,
			...(suggestedValidTo !== undefined ? { validTo: suggestedValidTo } : {}),
		});
	};

	/**
	 * Suggests a `validTo` date when `validFrom` changes, using whichever subscription type is
	 * already selected. Together with {@link handleProductSubscriptionTypeIdChange}, this replaces
	 * a `useEffect` that watched both `validFrom` and the derived `selectedProductSubscriptionType`
	 * and wrote `validTo` as a side effect.
	 */
	const handleValidFromChange = (value: Date | null) => {
		const suggestedValidTo = computeSuggestedValidTo(
			value,
			form.values.validTo,
			selectedProductSubscriptionType?.durationInDays,
			selectedProductSubscriptionType?.isTrialSubscription,
		);
		form.setValues({
			validFrom: value,
			...(suggestedValidTo !== undefined ? { validTo: suggestedValidTo } : {}),
		});
	};

	const handleSubmit = async (values: KlaviyoTestFormValues) => {
		if (!values.metric) {
			return;
		}
		const loader = addLoader();
		try {
			await FetchUtils.postJson('/api/admin/klaviyo-test/trigger', {
				metric: values.metric,
				email: values.email,
				uniquifyEmail: values.uniquifyEmail,
				customerName: values.customerName || undefined,
				productId: values.productId ?? undefined,
				productSubscriptionTypeId: values.productSubscriptionTypeId ?? undefined,
				fromProductId: values.fromProductId ?? undefined,
				fromProductSubscriptionTypeId: values.fromProductSubscriptionTypeId ?? undefined,
				validFrom: values.validFrom ? values.validFrom.toISOString() : undefined,
				validTo: values.validTo ? values.validTo.toISOString() : undefined,
				automaticRenew: values.automaticRenew,
				transitionDirection: values.transitionDirection || undefined,
				resetUrl: values.resetUrl || undefined,
				audienceType: values.audienceType || undefined,
				parentInterest: values.parentInterest || undefined,
				professionalType: values.professionalType || undefined,
				signupSource: values.signupSource || undefined,
				signupContextUrl: values.signupContextUrl || undefined,
				brandingType: values.brandingType || undefined,
				userTimezone: values.userTimezone || undefined,
				paymentId: values.paymentId || undefined,
				invoiceDownloadUrl: values.invoiceDownloadUrl || undefined,
				programmeId: values.programmeId ?? undefined,
				eventOccurrenceId: values.eventOccurrenceId ?? undefined,
				quizId: values.quizId ?? undefined,
				// The UI captures a percentage; the API and the real emit use a 0–1
				// fraction. Only QUIZ_PASSED consumes score — omit it otherwise so the
				// request only carries fields the metric needs.
				score: requirements?.needsQuiz && values.score !== '' ? values.score / 100 : undefined,
				paymentAmountInCents: values.paymentAmountInCents === '' ? undefined : values.paymentAmountInCents,
				paymentCurrency: values.paymentCurrency || undefined,
				accountUrl: values.accountUrl || undefined,
			}).response;

			NotificationUtils.showSuccess(
				`Queued ${labelFor(metadata, values.metric)} for ${values.email}`,
				'Event queued',
			);
			setActivityLog((log) => [
				{
					id: `${Date.now()}`,
					sentAt: new Date(),
					metric: values.metric as KlaviyoMetricType,
					email: values.email,
					success: true,
					message: 'Queued successfully',
				},
				...log,
			]);
		} catch (err) {
			const error = err as Error;
			NotificationUtils.showError(error, 'Failed to trigger Klaviyo test event');
			setActivityLog((log) => [
				{
					id: `${Date.now()}`,
					sentAt: new Date(),
					metric: values.metric as KlaviyoMetricType,
					email: values.email,
					success: false,
					message: error.message,
				},
				...log,
			]);
		} finally {
			removeLoader(loader);
		}
	};

	const isDisabled = isLoading || metadata === null;

	return (
		<StyledKlaviyoTestForm className={`KlaviyoTestForm ${className ?? ''}`}>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack>
					<Select
						{...form.getInputProps('metric')}
						onChange={handleMetricChange}
						label="Event to trigger"
						description="Pick the Klaviyo event you want to test"
						placeholder="Select an event"
						data={metricOptions}
						disabled={isDisabled}
						searchable
						withAsterisk
					/>
					<TextInput
						{...form.getInputProps('email')}
						type="email"
						label="Recipient email"
						description="Who should receive this test event"
						autoComplete="email"
						disabled={isDisabled}
						withAsterisk
					/>
					<Checkbox
						{...form.getInputProps('uniquifyEmail', { type: 'checkbox' })}
						label="Uniquify email (plus-addressing)"
						description="Appends +timestamp to the email so Klaviyo's Smart Sending doesn't suppress repeat sends"
						disabled={isDisabled}
					/>
					<TextInput
						{...form.getInputProps('customerName')}
						label="Customer name"
						description="Optional — used to personalise the event"
						disabled={isDisabled}
					/>

					{requirements?.needsProduct && (
						<ProductPlanFields
							form={form}
							disabled={isDisabled}
							productOptions={productOptions}
							productSubscriptionTypeOptions={productSubscriptionTypeOptions}
							onProductIdChange={handleProductIdChange}
							onProductSubscriptionTypeIdChange={handleProductSubscriptionTypeIdChange}
						/>
					)}

					{requirements?.needsFromProduct && (
						<FromProductPlanFields
							form={form}
							disabled={isDisabled}
							productOptions={productOptions}
							fromProductSubscriptionTypeOptions={fromProductSubscriptionTypeOptions}
							onFromProductIdChange={handleFromProductIdChange}
						/>
					)}

					{requirements?.needsDates && (
						<DateRangeFields form={form} disabled={isDisabled} onValidFromChange={handleValidFromChange} />
					)}

					{requirements?.needsAutoRenew && (
						<Checkbox
							{...form.getInputProps('automaticRenew', { type: 'checkbox' })}
							label="Automatic renew"
							description="Toggles between the RENEWING_* and EXPIRING_* variants of this event"
							disabled={isDisabled}
						/>
					)}

					{requirements?.needsTransitionDirection && (
						<TextInput
							{...form.getInputProps('transitionDirection')}
							label="Transition direction"
							disabled={isDisabled}
						/>
					)}

					{requirements?.needsResetUrl && (
						<TextInput
							{...form.getInputProps('resetUrl')}
							label="Reset link"
							withAsterisk
							disabled={isDisabled}
						/>
					)}

					{requirements?.needsNewsletterFields && <NewsletterFields form={form} disabled={isDisabled} />}

					{requirements?.needsPayment && <PaymentFields form={form} disabled={isDisabled} />}

					{requirements?.metric === KlaviyoMetricType.PAYMENT_FAILED && (
						<PaymentFailureFields form={form} disabled={isDisabled} />
					)}

					{requirements?.needsProgramme && (
						<ProgrammeField
							form={form}
							disabled={isDisabled}
							programmeOptions={programmeOptions}
							onProgrammeIdChange={handleProgrammeIdChange}
						/>
					)}

					{requirements?.needsAccessProduct && (
						<AccessProductField form={form} disabled={isDisabled} productOptions={accessProductOptions} />
					)}

					{requirements?.needsEventOccurrence && (
						<>
							<EventOccurrenceField
								form={form}
								disabled={isDisabled}
								eventOccurrenceOptions={eventOccurrenceOptions}
							/>
							<RegistrantTimezoneField form={form} disabled={isDisabled} />
						</>
					)}

					{requirements?.needsQuiz && (
						<QuizFields form={form} disabled={isDisabled} quizOptions={quizOptions} />
					)}

					<Group justify="flex-end" mt="md">
						<Button type="submit" disabled={isDisabled}>
							Trigger event {isLoading && <Loader size="xs" ml="xs" />}
						</Button>
					</Group>
				</Stack>
			</form>

			<KlaviyoTestActivityLog activityLog={activityLog} labelFor={(metric) => labelFor(metadata, metric)} />
		</StyledKlaviyoTestForm>
	);
}

function groupMetricsByCategory(metrics: KlaviyoTestEventFieldRequirements[]) {
	const byCategory = new Map<string, { value: string; label: string }[]>();
	for (const metric of metrics) {
		const items = byCategory.get(metric.category) ?? [];
		items.push({ value: metric.metric, label: metric.label });
		byCategory.set(metric.category, items);
	}
	return Array.from(byCategory.entries()).map(([group, items]) => ({ group, items }));
}

const StyledKlaviyoTestForm = styled.div`
	form {
		max-width: 520px;
	}
`;
